<template>
  <a
    ref="cpcLink"
    class="cpc-link"
    :class="{ 'no-events': !isReady || disabled }"
    rel="nofollow"
    href="#"
    target="_blank"
    draggable="false"
    @pointerdown="onPointerDown"
    @pointeenter="onPointerOver"
    @click="toLink"
    @auxclick="onAuxClick"
  >
    <slot />
    <span v-if="!isReady" class="preload-block" />
  </a>
</template>

<script lang="ts" setup>
import type { ModelOfferItem } from '~/interfaces/api';
import { useGlobalStore } from '~/stores/global';
import { useAnalyticsStore } from '~/stores/analytics';
import { useTimeCalculation } from '~/composition/useTimeCalculation';

/**
 * статься по работе в confluence
 * https://confluence.price-co.ru/pages/viewpage.action?pageId=49289437
 * Компонент cpc-link предназначен для переходов на сторонние ресурсы (как правило в мазагины)
 * Предусматривает защиту от двойного клика, переход по ссылке нажатием кнопки скролла и отправку аналитики
 * @param {to} ссылка для перехода
 * @param {offer} информация о магазине (товаре)
 * @param {position} параметр slot для добавления в ссылку
 * @param {blockType} параметр block для добавления в ссылку
 */

const cpcLink = ref();
const globalStore = useGlobalStore();
const isReady = ref(globalStore.isBot);
const {
  $gtmEvent,
  $gtmOfferEvent,
  $gtagEvent,
  $ymGoalEvent,
  $ymCpcEvent,
  $DSPCounterEvent,
  $ymEcommerceEvent,
  $tmrSaleEvent,
} = useNuxtApp();
const config = useRuntimeConfig();
const emit = defineEmits(['sendAnalytics']);
const props = defineProps({
  to: {
    type: String,
    default: '',
  },
  offer: {
    type: Object as () => ModelOfferItem | undefined,
    required: true,
  },
  position: {
    type: Number,
    default: 1,
  },
  blockType: {
    type: String,
    default: 'unknown',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const analyticsStore = useAnalyticsStore();
const { breadcrumbs } = globalStore?.seoData?.page || {};
let mt = 0;
let mo = 0;

// защита от двойного клика
let preventClick = false;
const preventClickTimeout = 3000; // in ms

const isRelativeLink: boolean =
  !!props.to && props.to.match(/^(?:[a-z]+:)?\/\//i) === null;

// формирование ссылки для перехода
function onPointerDown() {
  if (cpcLink.value && props.to) {
    if (isRelativeLink) {
      let origin = config.public.VITE_CPC_PREFIX || 'https://price.ru';
      if (!import.meta.env.SSR && window) {
        origin = `https://${window.location.hostname}`;
      }
      //  ссылка редиректит на беке
      //  https://confluence.price-co.ru/pages/viewpage.action?pageId=49290449 ...
      const md = Date.now();
      const { to, position } = props;
      const session = analyticsStore.session.id;
      const pageView = analyticsStore.pageviewId;
      const offerID = props.offer?.id || '';
      const st =
        offerID && analyticsStore.cpcshows[offerID]
          ? analyticsStore.cpcshows[offerID]
          : '';
      const type = props.blockType;
      const wd = navigator?.webdriver ? 1 : 0;
      cpcLink.value.href = `${origin}${to}&slot=${position}&session_id=${session}&pageview_id=${pageView}&block=${type}&mt=${mt}&md=${md}&mo=${mo}&wd=${wd}&st=${st}`;
    } else {
      cpcLink.value.href = props.to;
    }
  }
}

// отправка аналитики
const sendAnalyticsFunc = (offer: ModelOfferItem) => {
  const { cost, model_id, price, shop_info } = offer;
  emit('sendAnalytics', offer);
  $gtmEvent(cost, shop_info?.id || 0, price);
  $gtmOfferEvent(model_id, price);
  $ymCpcEvent();
  let monitoring = '';
  if (offer?.has_min_price) {
    monitoring = 'cheapest_monitoring';
  } else if (offer?.less_than_avg && offer.less_than_avg > 0) {
    monitoring = 'cheaper_monitoring';
  }
  $ymGoalEvent(
    cost,
    !!shop_info?.proved_merchant,
    !!shop_info?.merchant_rating,
    props.blockType,
    props.position,
    monitoring
  );
  $gtagEvent(cost);
  $DSPCounterEvent('shop', {
    '10': model_id,
  });
  const category = breadcrumbs?.length > 2 ? breadcrumbs[2]?.title : null;
  const brand = breadcrumbs?.length > 2 ? breadcrumbs[3]?.title : null;
  $ymEcommerceEvent('add', {
    id: model_id,
    price,
    name: offer.name,
    ...(category ? { category } : {}),
    ...(brand ? { brand } : {}),
    other: 1,
  });
  $tmrSaleEvent(cost, model_id || null);
};

// переход по ссылке
const toLink = (event?: MouseEvent, isAuxClick?: boolean) => {
  const { offer } = props;
  if (offer) {
    if (preventClick && !isAuxClick) {
      event?.preventDefault();
      return false;
    }
    preventClick = true;
    analyticsStore.addOfferClick(offer.id.toString());

    if (isRelativeLink && cpcLink.value && cpcLink.value.href) {
      if (!cpcLink.value.href.includes('&ct=')) {
        cpcLink.value.href += `&ct=${Date.now()}`;
      }
      const clicks = analyticsStore.offersClick[offer.id];
      if (cpcLink.value.href.includes('&clk=')) {
        cpcLink.value.href = cpcLink.value.href.replace(
          /&clk=(\d+)/,
          `&clk=${clicks}`
        );
      } else {
        cpcLink.value.href += '&clk=' + clicks;
      }
    }
    sendAnalyticsFunc(offer);
    setTimeout(() => {
      preventClick = false;
    }, preventClickTimeout);
  }
};

// переход по ссылки при нажатии кнопки скролла
function onAuxClick(event: MouseEvent) {
  if (event.button === 1) {
    toLink(event, true);
  }
}

function onPointerOver() {
  mo++;
  mt = Date.now();
}

onMounted(() => {
  isReady.value = true;
});
useTimeCalculation();
</script>

<style scoped lang="scss">
a {
  text-decoration: none;
  color: inherit;
  border: none;
  display: block;
}

.no-events {
  position: relative;
  pointer-events: none;

  .preload-block {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      0.25turn,
      transparent,
      rgba(255, 255, 255, 1),
      transparent
    );
    border-radius: 3px;
    background-color: $gray1;
    background-repeat: no-repeat;
    background-size: 215px 100%;
    background-position: -215px 0;
    animation-duration: 0.5s;
    animation: loading 2s infinite;
  }
}

@keyframes loading {
  to {
    background-position: 300px 0;
  }
}
</style>
